import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthLayout from "@/pages/AuthLayout";
import GoogleButton from "@/pages/GoogleButton";

export default function Register() {
  const { t, lang } = useI18n();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data.user);
      navigate("/onboarding", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <AuthLayout title={t("createAccount")} subtitle={t("heroSub")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">{t("name")}</Label>
          <Input id="name" data-testid="register-name" required value={form.name} onChange={upd("name")} className="mt-1.5" placeholder="Rangga" />
        </div>
        <div>
          <Label htmlFor="email">{t("email")}</Label>
          <Input id="email" data-testid="register-email" type="email" required value={form.email} onChange={upd("email")} className="mt-1.5" placeholder="you@email.com" />
        </div>
        <div>
          <Label htmlFor="password">{t("password")}</Label>
          <Input id="password" data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={upd("password")} className="mt-1.5" placeholder="Min. 6 karakter" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("register")}
        </Button>
      </form>
      <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton />
      <p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground" data-testid="register-consent">
        {lang === "id" ? "Dengan mendaftar, kamu menyetujui " : "By signing up, you agree to the "}
        <Link to="/terms" className="font-medium text-primary hover:underline" data-testid="register-terms-link">{lang === "id" ? "Syarat Layanan" : "Terms of Service"}</Link>
        {lang === "id" ? " dan " : " and "}
        <Link to="/privacy" className="font-medium text-primary hover:underline" data-testid="register-privacy-link">{lang === "id" ? "Kebijakan Privasi" : "Privacy Policy"}</Link>
        {lang === "id" ? " FitBersama." : " of FitBersama."}
      </p>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t("haveAccount")} <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="to-login">{t("login")}</Link>
      </p>
    </AuthLayout>
  );
}
